import { capturePermissionSubject } from '@/stores/permissionsStore'
import { useAuthStore } from '@/stores/authStore'

/** Refuse any account transition, including A→B→A while Clerk obtains its token. */
export async function presentationHeaders(
  read: () => Promise<Record<string, string>>,
  owner: { tenantId: string; userId: string },
  signal?: AbortSignal,
) {
  let changed = false
  const unsubscribe = useAuthStore.subscribe((state) => {
    if (state.tenantId !== owner.tenantId || state.userId !== owner.userId) changed = true
  })
  let rejectAbort: (() => void) | undefined
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectAbort = () => reject(new Error('Presentation request timed out'))
    signal?.addEventListener('abort', rejectAbort, { once: true })
    if (signal?.aborted) rejectAbort()
  })
  let headers: Record<string, string>
  try {
    headers = await Promise.race([read(), aborted])
  } finally {
    unsubscribe()
    if (rejectAbort) signal?.removeEventListener('abort', rejectAbort)
  }
  const current = useAuthStore.getState()
  if (
    changed ||
    current.tenantId !== owner.tenantId ||
    current.userId !== owner.userId ||
    headers['X-Tenant-Id'] !== owner.tenantId ||
    headers['X-User-Id'] !== owner.userId
  )
    throw new Error('Obsolete presentation scope')
  return headers
}

/** Keep response ownership separate from current write grants: receipts remain readable. */
export function capturePresentationOwner(owner?: { tenantId: string; userId: string }) {
  const owns = owner ? capturePermissionSubject(owner.tenantId, owner.userId) : () => true
  return () => {
    if (!owns()) throw new Error('Obsolete presentation scope')
  }
}

/** Synchronous final authorization; never place this behind another promise continuation. */
export function authorizePresentationSend(
  authorizeOwner: () => void,
  beforeSend: (() => void) | undefined,
  onFailure: () => void,
) {
  try {
    authorizeOwner()
    beforeSend?.()
  } catch (error) {
    onFailure()
    throw error
  }
}
